import { supabase } from './supabase';
import * as bcrypt from 'bcryptjs';

export interface User {
  id: string;
  email: string;
  is_admin: boolean;
  is_online: boolean;
  wallet_balance: number;
  profile_image: string | null;
  username: string;
}

export async function loginUser(email: string, password: string): Promise<User> {
  try {
    // Get user from ponousers table
    const { data: users, error } = await supabase
      .from('ponousers')
      .select('*')
      .eq('email', email.toLowerCase());

    if (error) {
      throw new Error(handleSupabaseError(error));
    }
    
    if (!users || users.length === 0) {
      throw new Error('User not found');
    }

    const user = users[0];
    const isValid = await bcrypt.compare(password, user.password_hash);
    if (!isValid) {
      throw new Error('Invalid password');
    }

    // Update online status
    const { error: updateError } = await supabase
      .from('ponousers')
      .update({ is_online: true })
      .eq('id', user.id);

    if (updateError) {
      console.error('Failed to update online status:', updateError);
      // Don't throw here as login was successful
    }

    // Remove password_hash from user object before returning
    const { password_hash, ...userWithoutPassword } = user;
    
    // Store user in localStorage
    localStorage.setItem('user', JSON.stringify(userWithoutPassword));
    
    return userWithoutPassword as User;
  } catch (error: any) {
    throw new Error(error.message || 'Login failed');
  }
}

export async function registerUser(email: string, password: string): Promise<User> {
  try {
    // Check if user already exists
    const { data: existingUsers } = await supabase
      .from('ponousers')
      .select('id')
      .eq('email', email.toLowerCase());

    if (existingUsers && existingUsers.length > 0) {
      throw new Error('Email already registered');
    }

    const hashedPassword = await bcrypt.hash(password, 10);
    const username = email.split('@')[0]; // Generate username from email

    // Create user in ponousers table
    const { data: userData, error: userError } = await supabase
      .from('ponousers')
      .insert([
        {
          email: email.toLowerCase(),
          password_hash: hashedPassword,
          username: username,
          is_online: true,
          wallet_balance: 0,
          is_admin: false,
          profile_image: null
        }
      ])
      .select()
      .single();

    if (userError) throw new Error('Failed to create user');
    if (!userData) throw new Error('Failed to create user');

    // Remove password_hash from user object before returning
    const { password_hash, ...userWithoutPassword } = userData;

    // Store user in localStorage
    localStorage.setItem('user', JSON.stringify(userWithoutPassword));
    
    return userWithoutPassword as User;
  } catch (error: any) {
    throw new Error(error.message || 'Registration failed');
  }
}

export async function logoutUser(userId: string) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Update online status
    const { error } = await supabase
      .from('ponousers')
      .update({ is_online: false })
      .eq('id', userId);

    if (error) throw error;

    // Clear user data from localStorage
    localStorage.removeItem('user');
  } catch (error: any) {
    throw new Error(error.message || 'Logout failed');
  }
}

export function getCurrentUser(): User | null {
  const userJson = localStorage.getItem('user');
  if (!userJson) return null;
  
  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
}

// Helper function to handle Supabase errors
function handleSupabaseError(error: any) {
  if (error.code === 'PGRST116') {
    return 'No data found';
  }
  
  if (error.code === '23505') {
    return 'This record already exists';
  }
  
  if (error.code === '42501') {
    return 'You do not have permission to perform this action';
  }
  
  return error.message || 'An unexpected error occurred';
}